'use client'
import { PrismicRichText, PrismicImage, PrismicLink } from "@prismicio/react";
import { isFilled, asLink } from "@prismicio/client";
import { useState, useEffect, useRef } from "react";

const AccordionArrow = ({ bgColor, isOpen }) => {
  const colorMap = {
    'beige': '#731431',
    'dark-pink': '#FDF6D2',
    'light-pink': '#731431',
    'dark-green': '#DFE05B',
    'light-green': '#5E6723',
    'dark-orange': '#FCC453',
    'light-orange': '#FF6C24',
    'moyam-orange': '#FDF6D2',
  };

  const strokeColor = colorMap[bgColor] || '#FDF6D2';

  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`transition-transform w-5 h-5 md:w-10 md:h-10 duration-300 ${isOpen ? 'rotate-180' : ''}`}
    >
      <path d="M10 15L20 25L30 15" stroke={strokeColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const AccordionItem = ({ item, index, isOpen, onToggle, bgColor }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (isOpen && contentRef.current) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          setMaxHeight(`${contentRef.current.scrollHeight}px`);
        }
      });
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen]);

  return (
    <div className="pb-2">
      {isFilled.richText(item.title) && (
        <div
          className="flex justify-between items-center cursor-pointer gap-4 border-b-1 pb-1 md:pb-2"
          onClick={() => onToggle(index)}
        >
          <PrismicRichText
            field={item.title}
            components={{
              heading2: ({ children }) => <h2 className="h4">{children}</h2>,
              heading3: ({ children }) => <h3 className="h4">{children}</h3>,
              heading4: ({ children }) => <h4 className="h4">{children}</h4>,
            }}
          />
          <AccordionArrow bgColor={bgColor} isOpen={isOpen} />
        </div>
      )}
      {isFilled.richText(item.text) && (
        <div
          style={{ maxHeight }}
          className="overflow-hidden transition-all duration-500 ease-in-out pt-1 md:pt-2"
          ref={contentRef}
        >
          <PrismicRichText
            field={item.text}
            components={{
              paragraph: ({ children }) => <p className="p mb-3 md:mb-6 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="font-bold">{children}</strong>,
              hyperlink: ({ children, node }) => (
                <a href={node.data.url} target={node.data.target} className="underline-moyam">
                  {children}
                </a>
              ),
              list: ({ children }) => <ul className="-mt-2 md:-mt-3 mb-3">{children}</ul>,
              oList: ({ children }) => <ol>{children}</ol>,
              listItem: ({ children }) => <li className="p-li">{children}</li>,
              label: ({ node, children }) => {
                if (node.data.label === "number") {
                  return (
                    <span className="float-right">{children}</span>
                  );
                }
                return <span className={node.data.label }>{children}</span>;
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.TextImageSectionSlice} TextImageSectionSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<TextImageSectionSlice>} TextImageSectionProps
 * @type {import("react").FC<TextImageSectionProps>}
 */
const TextImageSection = ({ slice }) => {
  const isImgRight = slice.variation === "imgRight" || slice.variation === "acordionImgRight";
  const isAccordion = slice.variation === "acordion" || slice.variation === "acordionImgRight";

  // Vérifier s'il y a du contenu réel (pas juste un heading)
  const hasMainText = isFilled.richText(slice.primary.text) &&
    slice.primary.text.some(item => item.type === 'paragraph' || item.type === 'list-item' || item.type === 'o-list-item');

  const [openAccordionIndex, setOpenAccordionIndex] = useState(() => {
    if (isAccordion && isFilled.group(slice.primary.texts) && slice.primary.texts.length > 0) {
      // Si pas de contenu réel, ouvrir le premier
      return hasMainText ? null : 0;
    }
    return null;
  });

  const toggleAccordion = (index) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index);
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      id={slice.primary.anchor_id || undefined}
      className={`theme-${slice.primary.bg_color} `}
    >
      <div className="flex flex-col lg:!flex-row items-stretch 3xl:min-h-180 ">
        {isFilled.image(slice.primary.img) && (
          <div className={`hidden lg:flex w-1/2 grow-0 ${isImgRight ? 'order-2' : 'order-1'} h-auto relative overflow-hidden transition-all duration-500`}>
            <PrismicImage
              field={{
                ...slice.primary.img,
                alt: slice.primary.img.alt || "Image d'illustration"
              }}
              className="h-full w-full object-cover object-center md:absolute md:absolute-center transition-all duration-500"
            />
          </div>
        )}
        {isFilled.image(slice.primary.img_mobile) && (
          <div className={`lg:hidden w-full ${isImgRight ? 'order-2' : 'order-2'} md:h-120`}>
            <PrismicImage
              field={{
                ...slice.primary.img,
                alt: slice.primary.img.alt || "Image d'illustration"
              }}
              className="hidden md:!block h-full w-full object-cover object-center"
            />
            <PrismicImage
              field={{
                ...slice.primary.img_mobile,
                alt: slice.primary.img_mobile.alt || "Image d'illustration mobile"
              }}
              className="md:hidden object-cover object-center aspect-square w-full h-auto"
            />
          </div>
        )}

        <div className={`w-full lg:w-1/2 pt-8 pb-6 px-5 md:px-20 lg:pl-10 md:py-25 3xl:!px-30 flex flex-col 3xl:justify-center ${isImgRight ? 'order-1 lg:pl-20 3xl:!pl-20' : 'order-1 lg:order-2 3xl:!pr-20'}`}>
          {isFilled.richText(slice.primary.text) && (
            <div className="flex flex-col">
              <PrismicRichText
                field={slice.primary.text}
                components={{
                  paragraph: ({ children }) => <p className="p mb-3 md:mb-6 last:mb-0">{children}</p>,
                  heading1: ({ children }) => <h1 className="h2 mb-3 md:mb-6 last:mb-0">{children}</h1>,
                  heading2: ({ children }) => <h2 className="h2 mb-3 md:mb-6 last:mb-0">{children}</h2>,
                  heading3: ({ children }) => <h3 className="h4 mb-3 md:mb-6 last:mb-0">{children}</h3>,
                  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                  hyperlink: ({ children, node }) => (
                    <a href={node.data.url} target={node.data.target} className="underline-moyam">
                      {children}
                    </a>
                  ),
                  list: ({ children }) => <ul className="-mt-2 md:-mt-3 mb-3 md:mb-6 last:mb-0">{children}</ul>,
                  oList: ({ children }) => <ol>{children}</ol>,
                  listItem: ({ children }) => <li className="p-li">{children}</li>,
                  
                }}
              />
            </div>
          )}

          {isAccordion && isFilled.group(slice.primary.texts) && (
            <div className="mt-3 md:mt-6">
              {slice.primary.texts.map((item, index) => (
                <AccordionItem
                  key={index}
                  item={item}
                  index={index}
                  isOpen={openAccordionIndex === index}
                  onToggle={toggleAccordion}
                  bgColor={slice.primary.bg_color}
                />
              ))}
            </div>
          )}

          {(isFilled.link(slice.primary.cta) || isFilled.keyText(slice.primary.anchor)) && (() => {
            const hasLink = isFilled.link(slice.primary.cta);
            const hasAnchor = isFilled.keyText(slice.primary.anchor);
            const linkProps = {};
            
            if (hasLink && hasAnchor) {
              const ctaUrl = asLink(slice.primary.cta);
              linkProps.href = `${ctaUrl}#${slice.primary.anchor}`;
            } else if (hasLink) {
              linkProps.field = slice.primary.cta;
            } else if (hasAnchor) {
              linkProps.href = `#${slice.primary.anchor}`;
            }
            
            let Wrapper = 'div';
            if (hasLink && hasAnchor) {
              Wrapper = 'a';
            } else if (hasLink) {
              Wrapper = PrismicLink;
            } else if (hasAnchor) {
              Wrapper = 'a';
            }

            return (
              <Wrapper
                {...linkProps}
                className={`group inline-flex items-center  mt-3 md:mt-6 cta cta-${slice.primary.cta?.variant || 'moyam-orange'} ${slice.primary.bg_color === 'dark-pink' ? '!text-dark-pink' : ''}`}
              >
                {isFilled.image(slice.primary.icon) && isFilled.image(slice.primary.hover_icon) ? (
                  <span className="relative mr-2.5 w-5 h-5 md:w-6 md:h-6">
                    <PrismicImage
                      className="absolute inset-0 w-full h-full transition-opacity duration-200 opacity-100 group-hover:opacity-0"
                      field={{
                        ...slice.primary.icon,
                        alt: slice.primary.icon.alt || "Icône"
                      }}
                    />
                    <PrismicImage
                      className="absolute inset-0 w-full h-full transition-opacity duration-200 opacity-0 group-hover:opacity-100"
                      field={{
                        ...slice.primary.hover_icon,
                        alt: slice.primary.hover_icon.alt || "Icône"
                      }}
                    />
                  </span>
                ) : (
                  <>
                    {isFilled.image(slice.primary.icon) && (
                      <PrismicImage
                        className="w-5 h-5 md:w-6 md:h-6 mr-2.5"
                        field={{
                          ...slice.primary.icon,
                          alt: slice.primary.icon.alt || "Icône"
                        }}
                      />
                    )}
                    {!isFilled.image(slice.primary.icon) && isFilled.image(slice.primary.hover_icon) && (
                      <PrismicImage
                        className="w-5 h-5 md:w-6 md:h-6 mr-2.5"
                        field={{
                          ...slice.primary.hover_icon,
                          alt: slice.primary.hover_icon.alt || "Icône"
                        }}
                      />
                    )}
                  </>
                )}
                <span>{slice.primary.cta?.text || 'En savoir plus'}</span>
              </Wrapper>
            );
          })()}
        </div>
      </div>
    </section>
  );
};

export default TextImageSection;
